import React, { useEffect, useState } from 'react';
import { Category, Essencial } from '../types/Category';
import { createCategory, updateCategory, deleteCategory, marcarEssencial } from '../services/api';
import { useConfirmar } from '../contexts/Confirmacao';
import { mensagemDeErro } from '../utils/mensagemDeErro';
import { LIMITES } from '../utils/limites';
import Toast from './Toast';
import './GerenciadorDeCategorias.css';

/**
 * Criar, editar e excluir categoria — os dois botões e os painéis.
 *
 * ── Por que virou componente ─────────────────────────────────────────────
 *
 * Isto vivia inteiro dentro de `TransactionForm`: sete estados, quatro
 * handlers e cem linhas de JSX. Ela pediu o mesmo no card de Categorias, onde é
 * o lugar mais óbvio de procurar — e a segunda cópia teria sido a armadilha 4
 * deste projeto de novo, com duas telas divergindo no dia em que uma ganhasse
 * validação ou cor nova.
 *
 * ── Por que HOOK, e não componente ───────────────────────────────────────
 *
 * A primeira versão era um componente que devolvia os botões e os painéis
 * juntos, num fragmento. Não funciona: os botões pertencem a uma LINHA (o
 * cabeçalho do card, a linha do seletor) e os painéis pertencem ao BLOCO
 * abaixo dela.
 *
 * Renderizados juntos, os painéis caíram dentro da linha do cabeçalho — que é
 * `flex: nowrap` — e ficaram lado a lado com o botão "Lista": o cabeçalho
 * esticou de 34px para 264, e a lista de categorias virou uma coluna de 191px
 * espremida no meio. Foi o que ela viu.
 *
 * O hook devolve os dois pedaços separados, e quem chama põe cada um no lugar
 * certo. O estado continua sendo de quem gerencia.
 *
 * ── O que ele NÃO faz ────────────────────────────────────────────────────
 *
 * Não recarrega a lista. Quem chama é quem sabe de onde ela veio — no
 * formulário é o `App`, no gráfico é o mesmo `App` por outro caminho. Recarregar
 * aqui dentro faria duas telas buscarem a mesma coisa em momentos diferentes.
 */

/**
 * As três respostas, na ordem em que aparecem.
 *
 * "Ainda não sei" tem o mesmo peso visual das outras de propósito: é resposta
 * válida, e escondida como texto pequeno viraria a opção de quem desistiu.
 * Tocar na que já está marcada desfaz.
 */
const RESPOSTAS: { valor: Essencial; rotulo: string }[] = [
  { valor: 'essencial', rotulo: 'Essencial' },
  { valor: 'ajustavel', rotulo: 'Dá para ajustar' },
  { valor: 'nao_sei', rotulo: 'Ainda não sei' },
];

/**
 * O pedido de abrir o gerenciador, vindo de fora — hoje, do marcador do Finn.
 *
 * Evento e não prop porque quem pede (a bolha do chat) e quem abre (o card de
 * Categorias) não têm ancestral em comum que valha a pena atravessar.
 */
export const EVENTO_ABRIR_CATEGORIAS = 'fintracking:abrir-categorias';

const PRESET_COLORS = [
  '#ef4444', '#f97316', '#eab308', '#22c55e', '#14b8a6',
  '#3b82f6', '#6366f1', '#a855f7', '#ec4899', '#64748b',
];

interface Props {
  categories: Category[];
  /** Criou, editou ou excluiu — recarregue a lista. */
  aoMudar?: () => void;
  /** Só quem precisa selecionar a recém-criada usa. */
  aoCriar?: (id: string) => void;
  /** Só quem precisa reagir a uma exclusão usa. */
  aoExcluir?: (id: string) => void;
  /**
   * Abre o painel quando o Finn pede. Só UM lugar liga isto (o card de
   * Categorias): o formulário de lançamento usa o mesmo gerenciador, e os dois
   * abrindo juntos seriam duas listas iguais na tela.
   */
  abreAoPedir?: boolean;
}

export interface Gerenciador {
  /** Os dois botões. Vão numa linha — cabeçalho, ou ao lado do seletor. */
  botoes: React.ReactNode;
  /** Os painéis. Vão no bloco ABAIXO da linha, em largura cheia. */
  paineis: React.ReactNode;
}

export const useGerenciadorDeCategorias = ({
  categories, aoMudar, aoCriar, aoExcluir, abreAoPedir,
}: Props): Gerenciador => {
  const confirmar = useConfirmar();
  const [erro, setErro] = useState('');

  const [novaAberta, setNovaAberta] = useState(false);
  const [gerenciarAberto, setGerenciarAberto] = useState(false);

  const [nome, setNome] = useState('');
  const [cor, setCor] = useState(PRESET_COLORS[0]);
  const [salvando, setSalvando] = useState(false);

  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [nomeEditado, setNomeEditado] = useState('');
  const [corEditada, setCorEditada] = useState(PRESET_COLORS[0]);
  const [salvandoEdicao, setSalvandoEdicao] = useState(false);

  useEffect(() => {
    if (!abreAoPedir) return;
    const abrir = () => { setGerenciarAberto(true); setNovaAberta(false); };
    window.addEventListener(EVENTO_ABRIR_CATEGORIAS, abrir);
    return () => window.removeEventListener(EVENTO_ABRIR_CATEGORIAS, abrir);
  }, [abreAoPedir]);

  // Esc fecha o que estiver aberto — o mesmo que o ✕.
  useEffect(() => {
    if (!gerenciarAberto && !novaAberta) return;
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || editandoId) return;
      setGerenciarAberto(false);
      setNovaAberta(false);
    };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [gerenciarAberto, novaAberta, editandoId]);

  const marcar = async (cat: Category, valor: Essencial) => {
    try {
      // Tocar na que já está marcada desfaz: volta a "nunca respondeu".
      await marcarEssencial(cat.id, cat.essencial === valor ? null : valor);
      aoMudar?.();
    } catch (e) {
      setErro(mensagemDeErro(e, 'Não consegui salvar a marca.'));
    }
  };

  const criar = async () => {
    if (!nome.trim()) return;
    setSalvando(true);
    try {
      const criada = await createCategory({ name: nome.trim(), color: cor });
      aoMudar?.();
      aoCriar?.(criada.id);
      setNome('');
      setCor(PRESET_COLORS[0]);
      setNovaAberta(false);
    } catch (e) {
      setErro(mensagemDeErro(e, 'Não consegui criar a categoria.'));
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async (cat: Category) => {
    if (!await confirmar({
      titulo: 'Excluir esta categoria?',
      alvo: cat.name,
      texto: 'Os lançamentos dela ficam sem categoria, mas nenhum é apagado.',
      acao: 'Excluir', perigo: true,
    })) return;
    try {
      await deleteCategory(cat.id);
      aoExcluir?.(cat.id);
      aoMudar?.();
    } catch (e) {
      setErro(mensagemDeErro(e, 'Não consegui excluir a categoria.'));
    }
  };

  const abrirEdicao = (cat: Category) => {
    setEditandoId(cat.id);
    setNomeEditado(cat.name);
    setCorEditada(cat.color);
  };

  const fecharEdicao = () => {
    setEditandoId(null);
    setNomeEditado('');
    setCorEditada(PRESET_COLORS[0]);
  };

  const salvarEdicao = async (cat: Category) => {
    if (!nomeEditado.trim()) return;
    setSalvandoEdicao(true);
    try {
      await updateCategory(cat.id, { name: nomeEditado.trim(), color: corEditada });
      aoMudar?.();
      fecharEdicao();
    } catch (e) {
      setErro(mensagemDeErro(e, 'Não consegui editar a categoria.'));
    } finally {
      setSalvandoEdicao(false);
    }
  };

  const cores = (atual: string, escolher: (c: string) => void) => (
    <div className="gcat-cores">
      {PRESET_COLORS.map(c => (
        <button
          key={c}
          type="button"
          className={`gcat-cor ${atual === c ? 'selecionada' : ''}`}
          style={{ background: c }}
          onClick={() => escolher(c)}
          aria-label={`Cor ${c}`}
          aria-pressed={atual === c}
        />
      ))}
    </div>
  );

  const botoes = (
    <div className="gcat-botoes">
        <button
          type="button"
          className={`gcat-btn ${novaAberta ? 'ativo' : ''}`}
          onClick={() => { setNovaAberta(p => !p); setGerenciarAberto(false); }}
          title="Nova categoria"
          aria-label="Nova categoria"
          aria-expanded={novaAberta}
        >+</button>
        {categories.length > 0 && (
          <button
            type="button"
            className={`gcat-btn ${gerenciarAberto ? 'ativo' : ''}`}
            onClick={() => { setGerenciarAberto(p => !p); setNovaAberta(false); }}
            title={gerenciarAberto ? 'Fechar categorias' : 'Gerenciar categorias'}
            aria-label="Gerenciar categorias"
            aria-expanded={gerenciarAberto}
          >⚙</button>
        )}
    </div>
  );

  const paineis = (
    <>
      {gerenciarAberto && categories.length > 0 && (
        <div className="gcat-painel">
          {/*
            * Cabeçalho fixo com o ✕: a lista rola e o painel pode ser longo —
            * sem ele, a única saída era voltar lá em cima e achar a engrenagem.
            */}
          <div className="gcat-painel-topo">
            <span className="gcat-painel-titulo">Suas categorias</span>
            <button
              type="button"
              className="gcat-fechar"
              onClick={() => { setGerenciarAberto(false); fecharEdicao(); }}
              title="Fechar"
              aria-label="Fechar categorias"
            >✕</button>
          </div>
          <p className="gcat-ajuda">
            Diga o que é essencial e o que dá para ajustar. O relatório e o Finn
            param de sugerir mexer no que não tem como mexer.
          </p>
          {categories.map(cat => (
            <div key={cat.id}>
              {editandoId === cat.id ? (
                <div className="gcat-form">
                  <input
                    type="text"
                    maxLength={LIMITES.nome}
                    value={nomeEditado}
                    onChange={e => setNomeEditado(e.target.value)}
                    className="gcat-input"
                    autoFocus
                  />
                  {cores(corEditada, setCorEditada)}
                  <div className="gcat-acoes">
                    <button type="button" className="gcat-cancelar" onClick={fecharEdicao}>
                      Cancelar
                    </button>
                    <button
                      type="button"
                      className="gcat-salvar"
                      onClick={() => salvarEdicao(cat)}
                      disabled={salvandoEdicao || !nomeEditado.trim()}
                      aria-busy={salvandoEdicao}
                    >
                      Salvar
                    </button>
                  </div>
                </div>
              ) : (
                <div className="gcat-item">
                  <span className="gcat-ponto" style={{ background: cat.color }} />
                  <span className="gcat-nome">{cat.name}</span>
                  <button
                    type="button"
                    className="gcat-editar"
                    onClick={() => abrirEdicao(cat)}
                    title={`Editar ${cat.name}`}
                    aria-label={`Editar ${cat.name}`}
                  >✎</button>
                  <button
                    type="button"
                    className="gcat-excluir"
                    onClick={() => excluir(cat)}
                    title={`Excluir ${cat.name}`}
                    aria-label={`Excluir ${cat.name}`}
                  >
                    {/* Lixeira, não ✕: o ✕ é de fechar o painel, logo acima. */}
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6M10 11v6M14 11v6" />
                    </svg>
                  </button>
                  <div className="gcat-essencial" role="group" aria-label={`${cat.name} é essencial?`}>
                    {RESPOSTAS.map(r => (
                      <button
                        key={r.valor}
                        type="button"
                        className={`gcat-resposta ${cat.essencial === r.valor ? 'marcada' : ''}`}
                        aria-pressed={cat.essencial === r.valor}
                        onClick={() => marcar(cat, r.valor)}
                      >{r.rotulo}</button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {novaAberta && (
        <div className="gcat-form gcat-nova">
          <input
            type="text"
            maxLength={LIMITES.nome}
            placeholder="Nome da categoria"
            value={nome}
            onChange={e => setNome(e.target.value)}
            className="gcat-input"
            autoFocus
          />
          {cores(cor, setCor)}
          <div className="gcat-acoes">
            <button type="button" className="gcat-cancelar" onClick={() => setNovaAberta(false)}>
              Cancelar
            </button>
            <button
              type="button"
              className="gcat-salvar"
              onClick={criar}
              disabled={salvando || !nome.trim()}
              aria-busy={salvando}
            >
              Criar
            </button>
          </div>
        </div>
      )}

      <Toast message={erro || null} onDismiss={() => setErro('')} />
    </>
  );

  return { botoes, paineis };
};

export { PRESET_COLORS };
